import { useState, useEffect, useRef, useCallback } from "react";
import { CallClient, Features } from "@azure/communication-calling"; //features is an extended feature of the core Call API.
import { AzureCommunicationTokenCredential } from "@azure/communication-common";
import { registerIncomingCallHandler, getDateTime } from "../utils/acs.incoming";
import { selfPhoneNumber } from "../../common/utils";
import { reportCallConnected, reportCallEnded } from "../../common/callLogReporter";
import {
  searchAndScreenPopOnly,
  logSalesforceActivity,
  updateSalesforceActivity,
  searchAndScreenPop,
  disableClickToDial,
  setSoftphonePanelVisibility,
  screenPop,
  enrichScreenPopData,
  generateFlowParams,
  getWhatIdListOnTask,
  setSoftphoneItemIcon,
} from "../utils/acs.salesforce";
import { useAnsweredElsewhereLogger } from "./useAnsweredElsewhereLogger";
import { muteCall, unmuteCall, holdCall, resumeCall } from "../utils/acs.callControls";
import { sendDtmfTone } from "../../common/acs.dtmf";
import { authenticatedFetch, getAcsToken } from "../../common/session"; //"../utils/session";
import {
  isOutboundScreenPopForClickToDialEnabled,
  isOutboundScreenPopForDialPadEnabled,
  getOutboundScreenPopFlow,
  isShowEditSubjectFieldEnabled,
  isTranslate911Enabled,
  getTranslated911Number,
  getTranslated933Number,
} from "../utils/callCenterSettings";
import {
  makeCallEndInfo,
  getDraftValues,
  getMetaWrapupValues,
  applyWrapupValuesToEndInfo,
  getVisibleCallEntry,
  getWrapupSavePayload,
} from "../utils/wrapupState";
import { OutboundCallToneController } from "../../common/outboundCallToneController";
// import { AzureLogger, setLogLevel } from "@azure/logger";

// // Set up logging
// setLogLevel("warning");
// AzureLogger.log = (...args) => console.log(...args);

function useACSClient(token, accountInfo) {
  const [teamsCallAgent, setTeamsCallAgent] = useState(null);
  const [deviceManager, setDeviceManager] = useState(null);
  const clickToDialEnabled = useRef(false);
  const clickToDialRegistered = useRef(false);
  const hvsRegistered = useRef(false);
  const hvsWorkId = useRef(null);
  const teamsCallAgentRef = useRef(teamsCallAgent);
  const deviceManagerRef = useRef(deviceManager);
  let lastDialTime = 0;
  const [callMap, setCallMap] = useState(new Map()); //in (ans) +out (ring/ ans)
  const callMapRef = useRef(callMap);
  const [incomingCallMap, setIncomingCallMap] = useState(new Map()); //in ring
  const incomingCallMapRef = useRef(incomingCallMap); //to track the latest map value for reading
  const activeConnectedCallIdRef = useRef(null);
  const [activeConnectedCallId, _setActiveConnectedCallId] = useState(null);
  const wrapupConnectedCallIdRef = useRef(null);
  const [wrapupConnectedCallId, _setWrapupConnectedCallId] = useState(null);
  const [call, setCall] = useState(null);
  const [transferredCall, setTransferredCall] = useState(null);
  const [transferCallMap, setTransferCallMap] = useState(new Map());
  const transferCallMapRef = useRef(transferCallMap);
  const [incomingCall, setIncomingCall] = useState(null); //only for inbound ringing state
  const [callState, setCallState] = useState(null);
  const [isSwitchingCall, setIsSwitchingCall] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isHold, setIsHold] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [notes, _setNotes] = useState("");
  const [subject, _setSubject] = useState("");
  const subjectRef = useRef("");
  const showEditSubject = isShowEditSubjectFieldEnabled();
  const [onAnotherDevice, setonAnotherDevice] = useState(false);
  const [callTransferState, setCallTransferState] = useState(null);
  const [callerInfo, setCallerInfo] = useState({
    callerNumber: null,
    callerName: null,
    matchedRecord: null,
    direction: null,
  });
  const [transferorInfo, setTransferorInfo] = useState(null);
  const callStartTimeRef = useRef(null);
  const lastCallMetaRef = useRef({ direction: null, number: null, name: null });
  const lastEntityIdRef = useRef(null);
  const notesRef = useRef("");
  const [activeCallDet, setActiveCallDet] = useState(null);
  const [pickCallId, setPickCallId] = useState(null);
  const assignMetaRef = useRef({
    recordId: null,
    recordName: null,
    objectType: null,
  });
  const deletedAssignedRecIdRef = useRef("");
  const [callResult, _setCallResult] = useState("");
  const callResultRef = useRef("");
  const [callResultGroup, _setCallResultGroup] = useState("");
  const callResultGroupRef = useRef("");
  const wrapupSaveInFlightRef = useRef(new Set());
  const finalizedCallIdsRef = useRef(new Set());
  const suppressWrapupUiCallIdsRef = useRef(new Set());
  const outboundCallToneControllerRef = useRef(null);

  const isEmptyObject = (v) =>
    v == null || typeof v !== "object" || Array.isArray(v) || Object.keys(v).length === 0;
  //const isNonEmptyObject = v => v != null && typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length > 0;

  const normalizePrevToMap = (prev) =>
    prev instanceof Map
      ? prev
      : Array.isArray(prev)
        ? new Map(prev)
        : prev && typeof prev === "object"
          ? new Map(Object.entries(prev))
          : new Map();

  const normalizeKey = (k) => {
    if (k === null || k === undefined) return null;
    const key = String(k).trim();
    if (!key || key === "undefined" || key === "null") return null;
    return key;
  };

  // create a normalized per-call object
  const makeDefaultCallRecord = ({ id } = {}) => ({
    id,
    acsCall: null, // raw ACS payload
    callState: null,
    callTransferState: null,
    transferType: null,
    transferredCall: null,
    transferTargetInfo: null,
    parentCallId: null,
    direction: null, //from lastCallMetaRef
    number: null, //from lastCallMetaRef
    name: null, //from lastCallMetaRef
    callerInfo: {
      callerNumber: null,
      callerName: null,
      matchedRecord: null,
      direction: null,
    },
    hvsCallId: null,
    whoId: null,
    whoName: null,
    whoType: null,
    whatId: null,
    whatName: null,
    whatType: null,
    subject: null,
    startedAt: null, //Date.now(),
    startedAtUtc: null,
    startedAtDateTimeStr: null,
    answeredAt: null, //Date.now(),
    endedAt: null, //Date.now(),
    endedAtUtc: null,
    updatedAt: Date.now(),
    transferorInfo: {
      displayName: null,
      callScenario: null,
    },
    isMuted: false,
    isHold: false,
    holdedAt: null,
    holdDurationTotal: 0,
    //ansDuration: 0, //already there on task
    notes: null,
    callResult: null,
    callResultGroup: null,
    isWrapup: false,
    isWrapupInEditMode: false,
    isWrapupSaving: false,
    activityId: null,
    callerGraphUserId: null,
    callFromPhone: null, //Inbound = Customer phonenum
    callFromUpn: null, //Inbound = Customer UPN = Other agent's UPN = internal call / transfer leg
    callToPhone: null, //Inbound = Agent's number
    callToUPN: null,
    callHandlerId: null,
    transferFromUpn: null,
    forwardToPhone: null,
    forwardToUPN: null,
  });

  const startCallOptions = {
    customContext: {
      voipHeaders: [{ key: "unaricInitOutboundFromSf", value: "true" }],
    },
  };

  //Keep the Ref in sync whenever the state updates
  useEffect(() => {
    incomingCallMapRef.current = incomingCallMap;
  }, [incomingCallMap]);

  useEffect(() => {
    callMapRef.current = callMap;
  }, [callMap]);

  useEffect(() => {
    transferCallMapRef.current = transferCallMap;
  }, [transferCallMap]);

  useEffect(() => {
    teamsCallAgentRef.current = teamsCallAgent;
  }, [teamsCallAgent]);

  useEffect(() => {
    deviceManagerRef.current = deviceManager;
  }, [deviceManager]);

  useEffect(() => {
    outboundCallToneControllerRef.current = new OutboundCallToneController();
    return () => {
      outboundCallToneControllerRef.current?.dispose();
      outboundCallToneControllerRef.current = null;
    };
  }, []);

  // Initialize ACS client when token is available
  useEffect(() => {
    // Skip if agent already exists
    if (teamsCallAgent) return;

    if (!token?.token || !accountInfo) return;

    /*let activeCallTransferFeature;

    const onActiveCallsUpdated = (args) => {
      console.log("activeCallsUpdated", args.activeCallDetails);
      setActiveCallDet(args.activeCallDetails);
      setonAnotherDevice(Boolean(args?.activeCallDetails?.length));
    };

    const onNoActiveCalls = () => {
      console.log("noActiveCalls");
      setonAnotherDevice(false);
    };

    const onConnectionStateChanged = (args) => {
      console.log("connectionStateChanged", args);
    };*/ //future - uncomment

    const initializeClient = async () => {
      try {
        const callClient = new CallClient();

        // Create credential with refresher
        const tokenCredential = new AzureCommunicationTokenCredential({
          token: token.token,
          refreshProactively: true,
          tokenRefresher: async () => {
            console.log("tokenRefresher: ACS SDK requested a fresh token.");
            const acsTokenData = await getAcsToken();
            if (!acsTokenData?.token) {
              throw new Error("SESSION_EXPIRED");
            }
            return acsTokenData.token;
          },
        });

        const agent = await callClient.createTeamsCallAgent(tokenCredential);
        setTeamsCallAgent(agent);
        // Set up device manager
        const devManager = await callClient.getDeviceManager();
        await devManager.askDevicePermission({ audio: true });
        setDeviceManager(devManager);

        const connectionState = agent.connectionState;
        console.log("connectionState", connectionState); // it may return either of 'Connected' | 'Disconnected'

        /*activeCallTransferFeature = agent.feature(Features.ActiveCallTransfer);

        // Register listeners
        agent.on("connectionStateChanged", onConnectionStateChanged);
        activeCallTransferFeature.on(
          "activeCallsUpdated",
          onActiveCallsUpdated
        );
        activeCallTransferFeature.on("noActiveCalls", onNoActiveCalls);*/ //future - uncomment

        // Listen for incoming calls
        registerIncomingCallHandler({
          agent,
          setIncomingCall,
          callMapRef,
          incomingCallMapRef,
          upsertIncomingCallMap,
          removeFromIncomingCallMap,
          upsertCallMap,
          removeFromCallMap,
          setCallState,
          setCallerInfo,
          setIsMuted,
          setErrorMessage,
          setonAnotherDevice,
          lastCallMetaRef,
          callStartTimeRef,
          lastEntityIdRef,
          assignMetaRef,
          setDeletedAssignedRecId,
          setIsHold,
          setTransferorInfo,
        });

        // Salesforce CTI setup
        if (typeof sforce !== "undefined") {
          sforce.opencti.setSoftphoneItemIcon({ key: "call" });
          sforce.opencti.enableClickToDial({
            callback: (result) => {
              console.log("Click to dial enabled?", result);
              clickToDialEnabled.current = true;
              if (!clickToDialRegistered.current) {
                clickToDialRegistered.current = true;
                sforce.opencti.onClickToDial({
                  listener: async (result) => {
                    if (clickToDialEnabled.current && result?.number) {
                      console.log("Click to dial:", result);
                      await placeCallInternal(
                        result?.number,
                        teamsCallAgentRef.current,
                        deviceManagerRef.current,
                        result,
                      );
                    }
                  },
                });
                if (!hvsRegistered.current) {
                  hvsRegistered.current = true;
                  if (sforce.opencti.hvs) {
                    sforce.opencti.hvs.onWorkStart({
                      listener: async (payload) => {
                        if (clickToDialEnabled.current) {
                          try {
                            if (payload) {
                              console.log(JSON.stringify(payload));
                              let workId = payload.workId; // Save the work ID
                              let whenVal = payload.completeWorkWhen; // Save the completion requirement like 'TaskSaved'
                              let toVal = payload.attributes.to; // Save the number to associate with onClickToDial
                              hvsWorkId.current = workId;
                            }
                          } catch (e) {
                            console.error("hsvWorkStartListener caused an exception:" + e, e);
                          }
                        }
                      },
                    });
                  }
                }
              }
            },
          });
        }
      } catch (error) {
        console.error("Failed to initialize ACS client:", error);
        // setErrorMessage("Failed to initialize calling client.");
      }
    };

    initializeClient();

    return () => {
      // Cleanup
      /*if (activeCallTransferFeature) {
        activeCallTransferFeature.off(
          "activeCallsUpdated",
          onActiveCallsUpdated
        );
        activeCallTransferFeature.off("noActiveCalls", onNoActiveCalls);
      }*/ //future - uncomment

      if (teamsCallAgent) {
        //teamsCallAgent.off("connectionStateChanged", onConnectionStateChanged); //future - uncomment
        teamsCallAgent.dispose();
      }
    };
  }, [token, teamsCallAgent, accountInfo]);

  /*useAnsweredElsewhereLogger({
    activeCallDet,
    call,
    onAnotherDevice,
    pickCallId,
    setActiveCallDet,
    setPickCallId,
    lastEntityIdRef
  });*/ //future - uncomment

  const createSubjectForCall = (meta, subject) => {
    let subjectNew = "";
    //applicable for answered calls
    let number = meta?.callerInfo?.callerNumber;
    let name = meta?.callerInfo?.matchedRecord?.name || meta?.callerInfo?.callerName || null;
    let callType = meta?.direction || "Call";
    if (!callType.includes("Call")) {
      callType += " Call";
    }
    const counterpart = number || name || "";
    subjectNew =
      subject ||
      callType +
        (counterpart
          ? meta?.direction === "Inbound"
            ? ` from ${counterpart}`
            : ` to ${counterpart}`
          : "");
    return subjectNew;
  };

  const applyDraftState = (values = {}, activeCallId) => {
    if (values && values.parentCallId) {
      values = values?.parentCallId ? callMapRef?.current?.get(values?.parentCallId) : null;
      activeCallId = values?.id || null;
    }
    setNotes(values?.notes || "");
    setSubject(values?.subject || "");
    setCallResult(values?.callResult || "");
    setCallResultGroup(values?.callResultGroup || "");
    if (activeCallId !== undefined) {
      setActiveConnectedCallId(activeCallId);
    }
  };

  const clearDraftState = () => {
    applyDraftState({}, null);
  };

  const persistDraftForCall = (callId, extraPatch = {}) => {
    if (!callId) {
      return;
    }

    upsertCallMap(callId, {
      ...getDraftValues({
        notesRef,
        callResultRef,
        callResultGroupRef,
        subjectRef,
      }),
      ...extraPatch,
    });
  };

  const showSelectedEntry = (entry, { showEndedUiState = false, utilPopoutCallback } = {}) => {
    if (!entry) {
      return;
    }

    const [key, value] = entry;
    if (showEndedUiState && value?.isWrapup) {
      showEndedCallUi(utilPopoutCallback);
    }
    applyDraftState(value, key);
  };

  const getNextVisibleEntryAfterDisconnect = (activeCallId) =>
    getVisibleCallEntry({
      callMap: callMapRef.current,
      excludedCallId: activeCallId,
      prefer: "non-wrapup-first",
    });

  const getNextVisibleEntryAfterWrapup = (wrapupCallId) =>
    getVisibleCallEntry({
      callMap: callMapRef.current,
      excludedCallId: wrapupCallId,
      prefer: "last",
    });

  const showEndedCallUi = (utilPopoutCallback) => {
    setPresenceInTeamsClient("Available");
    if (typeof sforce !== "undefined") {
      // For showing the call icon
      sforce.opencti.setSoftphoneItemIcon({ key: "call" });
      if (sforce.console) {
        sforce.console.setCustomConsoleComponentPopoutable(true, utilPopoutCallback);
      }
    }
  };

  //for inbound, this method is called after accepting call, while for outbound its called on dial
  const subscribeToCall = (activeCall) => {
    console.log("activeCall", activeCall);
    setPickCallId(activeCall.id);
    const activeCallKey = normalizeKey(activeCall?.id);
    if (activeCallKey) {
      finalizedCallIdsRef.current.delete(activeCallKey);
    }
    console.log(`Call Id: ${activeCall.id}`);
    console.log(`Call state: ${activeCall.state}`);
    upsertCallMap(activeCall.id, { callState: activeCall.state });

    activeCall.on("stateChanged", async () => {
      console.log(`Call state changed: ${activeCall.state}`);
      const activeCallKey = String(activeCall.id).trim();
      // Ignore late SDK events after the call was already removed from UI state.
      if (!callMapRef?.current?.has(activeCallKey)) {
        console.log(`Ignoring stale stateChanged event for removed call: ${activeCallKey}`);
        return;
      }
      upsertCallMap(activeCall.id, { callState: activeCall.state });

      const currentCallMeta = callMapRef.current.get(activeCallKey) || {};
      outboundCallToneControllerRef.current?.handleOutboundStateChange({
        callId: activeCallKey,
        direction: currentCallMeta?.direction,
        callState: activeCall.state,
        callEndReason: activeCall.callEndReason,
      });

      const utilPopoutCallback = (result) => {
        if (result.success) {
          console.log("The method was successfully invoked.");
        } else {
          console.log(
            "Error while invoking this method. Check the browser developer console for more information.",
          );
        }
      };

      if (activeCall.state === "Connected") {
        /*if (!callStartTimeRef.current) {
          callStartTimeRef.current = Date.now();
        }*/
        //will be used for outbound as in upsert, we will prevent override during inbound
        let connectedCallInfo = { answeredAt: Date.now() };
        if (showEditSubject) {
          let subNew = createSubjectForCall(currentCallMeta, currentCallMeta.subject);
          setSubject(subNew || "");
          connectedCallInfo.subject = subNew || "";
        }
        upsertCallMap(activeCall.id, connectedCallInfo);
        reportCallConnected(activeCall.id, callMapRef.current.get(activeCallKey));
        setSoftphoneItemIcon("call");

        if (callMapRef?.current?.size === 1) {
          setPresenceInTeamsClient("InACall");
          if (typeof sforce !== "undefined" && sforce.console) {
            sforce.console.setCustomConsoleComponentPopoutable(false, utilPopoutCallback);
          }

          if (typeof sforce !== "undefined") {
            sforce.opencti.setSoftphoneItemIcon({ key: "call" });
          }
        }
      } else if (activeCall.state === "Disconnected") {
        console.log(
          `Call ended, reason: code=${activeCall.callEndReason.code}, subCode=${activeCall.callEndReason.subCode}`,
        );

        const endTs = Date.now();
        const endedAtUtc = new Date(endTs).toISOString().split(".")[0] + "Z";

        const meta = callMapRef.current.get(activeCall.id) || {};
        reportCallEnded(activeCall.id, meta, endTs);
        if (meta?.transferredCall) {
          cancelTransfer(meta);
        }

        const isOutbound = meta?.direction === "Outbound";
        if (isOutbound) {
          if (activeCall?.callEndReason?.code === 404) {
            setErrorMessage("The number you called doesn't exist.");
          } else if (
            activeCall?.callEndReason?.code === 484 &&
            activeCall?.callEndReason?.subCode === 560484
          ) {
            setErrorMessage(
              "The person you're trying to reach is busy on another call. Try again later.",
            );
          } else if (activeCall?.callEndReason?.code === 486) {
            setErrorMessage("The number you called is Busy. Try again later.");
          }
        }
        let uiDraft = getDraftValues({
          notesRef,
          callResultRef,
          callResultGroupRef,
          subjectRef,
        });
        let metaWrapupValues = getMetaWrapupValues(meta);
        const suppressWrapupUi = suppressWrapupUiCallIdsRef.current.has(activeCallKey);

        let notesForActiveCall = metaWrapupValues.notes;
        let callResultForActiveCall = metaWrapupValues.callResult;
        let callResultGroupForActiveCall = metaWrapupValues.callResultGroup;
        let subjectForActiveCall = metaWrapupValues.subject;
        let endInfoObj = makeCallEndInfo(endTs, endedAtUtc);

        //const ringDurationSec = getRingDurTotalInSec(meta);
        const durationSec = getAnsDurTotalInSec(meta, endTs);
        //const holdDurationSec = getHoldDurTotalInSec(meta, endTs);

        if (suppressWrapupUi) {
          // This call was intentionally terminated before starting another call.
          // Do not let late disconnect events repopulate wrapup values on the UI.
          if (showEditSubject && !metaWrapupValues?.subject && meta?.answeredAt) {
            metaWrapupValues.subject = createSubjectForCall(meta) || "";
            subjectForActiveCall = metaWrapupValues.subject;
          }
          endInfoObj = applyWrapupValuesToEndInfo(endInfoObj, metaWrapupValues);
          upsertCallMap(activeCall.id, endInfoObj);
          removeFromIncomingCallMap(activeCall.id);
          finalizeAndRemoveCall(activeCall.id);
        } else if (callMapRef?.current?.size === 1) {
          //no call pre-exists in wrapup, and this ended call can go in wrapup is its an answered call
          if (
            activeConnectedCallIdRef?.current &&
            activeConnectedCallIdRef.current == activeCall.id
          ) {
            notesForActiveCall = uiDraft.notes;
            callResultForActiveCall = uiDraft.callResult;
            callResultGroupForActiveCall = uiDraft.callResultGroup;
            if (showEditSubject && !uiDraft?.subject && meta?.answeredAt) {
              uiDraft.subject = createSubjectForCall(meta) || "";
              setSubject(uiDraft.subject);
            }
            subjectForActiveCall = uiDraft.subject;
            endInfoObj = applyWrapupValuesToEndInfo(endInfoObj, uiDraft);
          } else {
            if (showEditSubject && !metaWrapupValues?.subject && meta?.answeredAt) {
              metaWrapupValues.subject = createSubjectForCall(meta) || "";
              subjectForActiveCall = metaWrapupValues.subject;
            }
            endInfoObj = applyWrapupValuesToEndInfo(endInfoObj, metaWrapupValues);
            endInfoObj.isWrapup = true;
            endInfoObj.isWrapupInEditMode = true;
            applyDraftState(endInfoObj, activeCall.id);
          }
          upsertCallMap(activeCall.id, endInfoObj);

          showEndedCallUi(utilPopoutCallback);
          removeFromIncomingCallMap(activeCall.id);
          if (typeof durationSec === "number" && durationSec === 0) {
            //unanswered outbound call
            clearDraftState();
            finalizeAndRemoveCall(activeCall.id);
          } else {
            //Answered calls in/outbound - keep showing notes and wrapup, keep it in both active & wrapupid
            upsertCallMap(activeCall.id, {
              isWrapup: true,
              isWrapupInEditMode: true,
            });
            setWrapupConnectedCallId(activeCall.id);
          }
        } else if (callMapRef?.current?.size === 2) {
          if (typeof durationSec === "number" && durationSec === 0) {
            //unanswered call
            endInfoObj = applyWrapupValuesToEndInfo(endInfoObj, metaWrapupValues);
            upsertCallMap(activeCall.id, endInfoObj);

            //entry = other call
            const entry = getNextVisibleEntryAfterDisconnect(activeCall.id);

            if (entry) {
              //other call -> can be in wrapupMode or not
              const [key, value] = entry;
              let fetchDataFromOtherCall = false;
              if (value.isWrapup) {
                //other call in wrapup
                if (!value.isWrapupInEditMode) {
                  //expand wrapup portion
                  upsertCallMap(key, { isWrapupInEditMode: true });
                  fetchDataFromOtherCall = true;
                } else {
                  //wrapup portion already showing
                  setActiveConnectedCallId(key);
                }
              } else {
                //other call not in wrapup
                fetchDataFromOtherCall = true;
              }

              if (fetchDataFromOtherCall && value && key) {
                showSelectedEntry(entry);
              }
            }
            removeFromIncomingCallMap(activeCall.id);
            finalizeAndRemoveCall(activeCall.id);
          } else {
            //answered call -> do not remove from callmap
            //entry = other call
            const entry = getNextVisibleEntryAfterDisconnect(activeCall.id);

            if (entry) {
              //other call -> can be in wrapupMode or not
              const [key, value] = entry;
              if (value.isWrapup) {
                //other call in wrapup
                if (showEditSubject && !metaWrapupValues?.subject && meta?.answeredAt) {
                  metaWrapupValues.subject = createSubjectForCall(meta) || "";
                  subjectForActiveCall = metaWrapupValues.subject;
                }
                endInfoObj = applyWrapupValuesToEndInfo(endInfoObj, metaWrapupValues);
                endInfoObj.isWrapup = true;
                endInfoObj.isWrapupInEditMode = true;
                showEndedCallUi(utilPopoutCallback);
                //save other call
                //if other call with expanded wrapup -> fetch notes from screen -> save it -> remove it
                //else if other call with collapsed wrapup -> fetch notes from map  -> save it -> remove it
                await wrapupDone(value, true);
                upsertCallMap(activeCall.id, endInfoObj);
                applyDraftState(endInfoObj, activeCall.id);
                setWrapupConnectedCallId(activeCall.id);
              } else {
                //other call not in wrapup
                if (
                  activeConnectedCallIdRef?.current &&
                  activeConnectedCallIdRef.current == activeCall.id
                ) {
                  if (showEditSubject && !uiDraft?.subject && meta?.answeredAt) {
                    uiDraft.subject = createSubjectForCall(meta) || "";
                    setSubject(uiDraft.subject);
                  }
                  notesForActiveCall = uiDraft.notes;
                  callResultForActiveCall = uiDraft.callResult;
                  callResultGroupForActiveCall = uiDraft.callResultGroup;
                  subjectForActiveCall = uiDraft.subject;
                  endInfoObj = applyWrapupValuesToEndInfo(endInfoObj, uiDraft);
                  endInfoObj.isWrapup = true;
                  endInfoObj.isWrapupInEditMode = true;
                  //keep current call , which just ended as active and expand its wrapup
                  upsertCallMap(activeCall.id, endInfoObj);
                  setWrapupConnectedCallId(activeCall.id);
                } else {
                  //other call is active
                  if (showEditSubject && !metaWrapupValues?.subject && meta?.answeredAt) {
                    metaWrapupValues.subject = createSubjectForCall(meta) || "";
                    subjectForActiveCall = metaWrapupValues.subject;
                  }
                  endInfoObj = applyWrapupValuesToEndInfo(endInfoObj, metaWrapupValues);
                  endInfoObj.isWrapup = true;
                  endInfoObj.isWrapupInEditMode = false;
                  //keep current call in wrapup, do not expand
                  upsertCallMap(activeCall.id, endInfoObj);
                  setWrapupConnectedCallId(activeCall.id);
                }
              }
            }
            removeFromIncomingCallMap(activeCall.id);
          }
        } else {
          // > 2 call in callmap

          const entry = getNextVisibleEntryAfterDisconnect(activeCall.id);

          if (entry) {
            showSelectedEntry(entry, {
              showEndedUiState: true,
              utilPopoutCallback,
            });
          }
        }

        saveTask(
          meta,
          false,
          callResultForActiveCall,
          callResultGroupForActiveCall,
          notesForActiveCall,
          subjectForActiveCall,
          endTs,
          endedAtUtc,
        );
        suppressWrapupUiCallIdsRef.current.delete(activeCallKey);
        lastEntityIdRef.current = null;
        assignMetaRef.current = {
          recordId: null,
          recordName: null,
          objectType: null,
        };
        setDeletedAssignedRecId(null);
        callStartTimeRef.current = null;
        //setErrorMessage(""); //Commented as we are setting error message for specific call end reason
        /*if (typeof sforce !== "undefined" && (callMapRef?.current?.size === 1)) {
          sforce.opencti.setSoftphoneItemIcon({ key: "end_call" });
        }*/
      }
    });

    // Handle remote participants
    activeCall.remoteParticipants.forEach(subscribeToRemoteParticipant);
    activeCall.on("remoteParticipantsUpdated", (e) => {
      e.added.forEach(subscribeToRemoteParticipant);
      e.removed.forEach(() => console.log("Remote participant removed"));
    });
  };

  const upsertIncomingCallMap = (id, patch = {}) => {
    setIncomingCallMap((prev) => {
      const base = normalizePrevToMap(prev);
      const next = new Map(base);

      const key = normalizeKey(id);
      if (!key) return prev;
      const existing = next.get(key) ?? makeDefaultCallRecord({ id: key });

      // start from existing and only apply explicit patch keys
      const merged = { ...existing };

      for (const k of Object.keys(patch)) {
        if (k === "callerInfo") {
          const patchCaller = patch.callerInfo || {};
          const baseCaller = existing.callerInfo || {};
          merged.callerInfo = {
            ...baseCaller,
            ...patchCaller,
            matchedRecord: {
              ...((baseCaller && baseCaller.matchedRecord) || {}),
              ...((patchCaller && patchCaller.matchedRecord) || {}),
            },
          };
        } else {
          // generic: set whatever the patch provides (including acsCall if present)
          if (k === "startedAt" || k === "endedAt" || k === "answeredAt") {
            // || k === 'holdedAt'
            if (!merged[k]) merged[k] = patch[k];
          } else {
            merged[k] = patch[k];
          }
        }
      }

      merged.id = key;
      merged.updatedAt = Date.now();
      next.set(key, merged);
      return new Map(next);
    });
  };

  const removeFromIncomingCallMap = (id) => {
    setIncomingCallMap((prev) => {
      const key = normalizeKey(id);
      if (!key) return prev;
      if (!prev || !prev.has(key)) return prev;

      const next = new Map(prev);
      next.delete(key);
      return new Map(next);
    });

    /*// If you cache per-id handlers or refs, delete them here:
      // acceptHandlersRef.current.delete(id);
      // hangUpHandlersRef.current.delete(id);
    }, []);*/
  };

  const finalizeAndRemoveCall = (id) => {
    const key = normalizeKey(id);
    if (!key) return;
    finalizedCallIdsRef.current.add(key);
    removeFromCallMap(key);
  };

  const upsertCallMap = (id, patch = {}) => {
    setCallMap((prev) => {
      const base = normalizePrevToMap(prev);
      const next = new Map(base);

      const key = normalizeKey(id);
      if (!key) return prev;
      if (finalizedCallIdsRef.current.has(key)) {
        const isCallRecreated =
          Object.prototype.hasOwnProperty.call(patch, "acsCall") && !!patch.acsCall;
        if (!isCallRecreated) {
          return prev;
        }
        finalizedCallIdsRef.current.delete(key);
      }
      const existing = next.get(key) ?? makeDefaultCallRecord({ id: key });

      // start from existing and only apply explicit patch keys
      const merged = { ...existing };

      for (const k of Object.keys(patch)) {
        if (k === "callerInfo") {
          const patchCaller = patch.callerInfo || {};
          const baseCaller = existing.callerInfo || {};
          merged.callerInfo = {
            ...baseCaller,
            ...patchCaller,
            matchedRecord: {
              ...((baseCaller && baseCaller.matchedRecord) || {}),
              ...((patchCaller && patchCaller.matchedRecord) || {}),
            },
          };
        } else {
          // generic: set whatever the patch provides (including acsCall if present)
          if (k === "startedAt" || k === "endedAt" || k === "answeredAt") {
            // || k === 'holdedAt'
            if (!merged[k]) merged[k] = patch[k]; //Date.now();
          } else {
            merged[k] = patch[k];
          }
        }
      }

      merged.id = key;
      merged.updatedAt = Date.now();
      next.set(key, merged);
      return new Map(next);
    });
  };
  //}, []);

  const removeFromCallMap = (id) => {
    setCallMap((prev) => {
      const key = normalizeKey(id);
      if (!key) return prev;
      if (!prev || !prev.has(key)) return prev;

      const next = new Map(prev);
      next.delete(key);
      return new Map(next);
    });
  };

  const upsertTransferCallMap = (id, patch = {}) => {
    setTransferCallMap((prev) => {
      const base = normalizePrevToMap(prev);
      const next = new Map(base);

      const key = normalizeKey(id);
      if (!key) return prev;
      const existing = next.get(key) ?? makeDefaultCallRecord({ id: key });

      // start from existing and only apply explicit patch keys
      const merged = { ...existing };

      for (const k of Object.keys(patch)) {
        if (k === "callerInfo") {
          const patchCaller = patch.callerInfo || {};
          const baseCaller = existing.callerInfo || {};
          merged.callerInfo = {
            ...baseCaller,
            ...patchCaller,
            matchedRecord: {
              ...((baseCaller && baseCaller.matchedRecord) || {}),
              ...((patchCaller && patchCaller.matchedRecord) || {}),
            },
          };
        } else {
          // generic: set whatever the patch provides (including acsCall if present)
          if (k === "startedAt" || k === "endedAt" || k === "answeredAt") {
            // || k === 'holdedAt'
            if (!merged[k]) merged[k] = patch[k];
          } else {
            merged[k] = patch[k];
          }
        }
      }

      merged.id = key;
      merged.updatedAt = Date.now();
      next.set(key, merged);
      return new Map(next);
    });
  };

  const removeFromTransferCallMap = (id) => {
    setTransferCallMap((prev) => {
      const key = normalizeKey(id);
      if (!key) return prev;
      if (!prev || !prev.has(key)) return prev;

      const next = new Map(prev);
      next.delete(key);
      return new Map(next);
    });
  };

  /*const getPresenceStateLabel = async () => {
    try {
      // Session-based: credentials include session cookie, no need for Authorization header
      const response = await fetch("/api/getPresence", {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json"
        },
      });

      if (response.status === 401) {
        console.error('getPresenceStateLabel: Session expired, please login again.');
        return { success: false, message: "Session expired. Please login again.", error: "SESSION_EXPIRED" };
      }

      const data = await response.json();

      if (response.ok && data?.success) {
        // Normalized success structure
        return {
          success: true,
          message: data.message,
          availability: data.availability,
          activity: data.activity,
          graph: data.graph,
        };
      } else {
        console.error("Error from getPresenceStateLabel:", data);
        return {
          success: false,
          message: data.message || "Graph call failed",
          error: data.error || "Unknown error",
        };
      }
    } catch (err) {
      console.error("getPresenceStateLabel exception:", err);
      return { success: false, message: "Exception: ", error: err.message };
    }
  };*/

  const blindTransfer = async (str, call) => {
    if (!call || !call.acsCall) return;
    let targetUserId = "";
    try {
      let tmp = str.split("#mirage#");
      if (tmp && tmp.length > 0) {
        targetUserId = tmp[0];
      }

      console.log(call.acsCall.features);
      const transferFeature = call.acsCall.feature(Features.Transfer);

      const targetUser = {
        [tmp[1]]: targetUserId,
      };

      let forwardToPhone = null,
        forwardToUPN = null;
      if (tmp[1] == "microsoftTeamsUserId") {
        // 'cloud' is usually 'public' unless using DOD/GCC
        targetUser["cloud"] = "public";
        forwardToUPN = tmp[2];
      } else {
        forwardToPhone = targetUserId;
      }

      // Step 1: Put the call on hold, else transfer does not work, seems like sdk check call state internally
      if (!call?.isHold) {
        //for complete CT/ user resumed customer manually before bt
        await onHold(call); //on ui resume is shown we do not want it so do not set flag for hold
      }

      // Step 2: Perform the transfer
      //if the target doesn't answer, the call follows the target's existing forwarding and unanswered settings (e.g., it might go to 3CX voicemail)
      const options = { disableForwardingAndUnanswered: false };

      options["customContext"] = {
        voipHeaders: [{ key: "unaricIsBt", value: "true" }],
      };
      if (tmp?.length === 4) {
        //direct BT
        if (call?.id) {
          options["customContext"].voipHeaders.push({
            key: "unaricParentCallIdInTransfer",
            value: call.id,
          });
        }
        if (accountInfo?.upn) {
          options["customContext"].voipHeaders.push({
            key: "unaricCallerUpn",
            value: accountInfo.upn,
          });
        }
      } else if (tmp?.length === 5) {
        //bt for complete CT
        if (tmp[4]) {
          options["customContext"].voipHeaders.push({
            key: "unaricParentCallIdInTransfer",
            value: tmp[4],
          });
        }
        if (accountInfo.upn) {
          options["customContext"].voipHeaders.push({
            key: "unaricCallerUpn",
            value: accountInfo.upn,
          });
        }
      }

      const transfer = await transferFeature.transfer({ targetParticipant: targetUser }, options);

      // Do not immediately hang up Agent 1's leg, else transfer does not init

      // Listen for the actual outcome
      transfer.on("stateChanged", async () => {
        console.log(`Transfer State: ${transfer.state}`);
        //setCallTransferState(transfer.state);
        upsertTransferCallMap(transfer.id, { callState: transfer.state });
        upsertCallMap(call.id, {
          callTransferState: transfer.state,
          forwardToPhone,
          forwardToUPN,
        });
        // Possible states: 'None', 'Transferring', 'Transferred', 'Failed'
        if (transfer.state === "Transferring") {
          console.log("Agent 1 Transferring tp Agent 2");
          //if we hangup on Transferring, bt is not initiated, err = call is not connected yet
        } else if (transfer.state === "Transferred") {
          //called when agent 2 rejects/ does not pick up/ pick up - cannot differ
          // Step 3: Hang up Agent 1's leg
          await call.acsCall.hangUp(); //await hangUp(call.id);
          console.log("Agent 1 disconnected immediately after transfer");
        } else if (transfer.state === "Failed") {
          console.error("Transfer failed with code:", transfer.error);
          console.error(
            `Transfer failed. Code: ${transfer.error.code}, Subcode: ${transfer.error.subcode}`,
          );
          console.log("stateChanged : BT failed, but Agent 1 disconnected immediately");
          await call.acsCall.hangUp(); // await hangUp(call.id);
        }
      });

      transfer.on("transferAccepted", async () => {
        console.log("Transfer accepted");
        // Step 3: Hang up Agent 1's leg
        await call.acsCall.hangUp(); //await hangUp(call.id);
      });

      transfer.on("transferFailed", async (error) => {
        console.error("Transfer failed:", error);
        /*The error object usually contains a code like:
        - TargetNotReachable → wrong ID or tenant mismatch
        - NotSupported → call type doesn’t support transfer
        - PermissionDenied → Graph permissions or Teams policy issue
        */
        // Step 3: Hang up Agent 1's leg
        console.log("BT failed, but Agent 1 disconnected immediately");
        await call.acsCall.hangUp(); //await hangUp(call.id);
      });
    } catch (err) {
      console.error("Error during transfer:", err);
      try {
        await call.acsCall.hangUp(); //await hangUp(call.id);
        console.log("exception : BT failed, but Agent 1 disconnected immediately");
      } catch (er) {
        console.error("exception Error during hangup call after bt failed:", er);
      }
    }
  };

  const cancelTransfer = async (call, transferredCall, isTransferLegAlreadyEnded) => {
    try {
      if (!call) {
        call = transferredCall?.parentCallId
          ? callMapRef?.current?.get(transferredCall?.parentCallId)
          : null;
      }

      if (!transferredCall && !isTransferLegAlreadyEnded) {
        transferredCall = getTransferredCall(call);
      }

      if (call) {
        removeFromTransferCallMap(transferredCall?.id || call.transferredCall);
        upsertCallMap(call.id, {
          transferredCall: null,
          callTransferState: null,
          transferType: null,
          forwardToPhone: null,
          forwardToUPN: null,
        });
      }

      if (!transferredCall || !transferredCall.acsCall || isTransferLegAlreadyEnded) return;
      await transferredCall.acsCall.hangUp();
    } catch (err) {
      console.error("Exception while trying to hang up transferred call in CT:", err);
    } finally {
      try {
        /*if(call && (call.callState === "LocalHold" || call.callState === "RemoteHold")) {
          await onResume(call);
        }*/
        //Commented as teams does not resume agent 1
      } catch (err) {
        console.error(
          "Exception while trying to resume initial call in CT after cancel transfer:",
          err,
        );
      }
    }
  };

  //New Approach using BT
  const completeConsultedTransfer = async (call) => {
    try {
      let transferTargetInfo = call?.transferTargetInfo ?? "";
      transferTargetInfo += "#mirage#" + (call.transferredCall ?? "");

      let transferredCall = getTransferredCall(call);
      if (!transferredCall || !transferredCall.acsCall) return;
      await transferredCall.acsCall.hangUp();
      upsertCallMap(call?.id, { transferTargetInfo: null });
      return await blindTransfer(transferTargetInfo, call);
    } catch (error) {
      console.error("completeConsultedTransfer failed:", error);
    }
  };

  const getTransferredCall = (call) => {
    return transferCallMapRef?.current?.get(call?.transferredCall) || {};
  };

  const consultedTransfer = async (str, call) => {
    if (!call || !call.acsCall) return;
    if (call.transferredCall) {
      completeConsultedTransfer(call);
      return;
    }
    let targetUserId = "";
    try {
      let tmp = str.split("#mirage#");
      if (tmp && tmp.length > 0) {
        targetUserId = tmp[0];
      }
      let forwardToPhone = null,
        forwardToUPN = null,
        ctNumToDial = null;
      const targetUser = { [tmp[1]]: targetUserId };
      if (tmp[1] == "microsoftTeamsUserId") {
        targetUser["cloud"] = "public";
        forwardToUPN = tmp[2];
      } else {
        forwardToPhone = targetUserId;
      }
      //Place on hold, sdk does not aumatically place call on hold, required else complete transfer fails
      //await call.acsCall.hold();

      if (!call?.isHold) {
        await onHold(call); //Added on ui resume is shown we do not want it so do not set flag for hold
      }

      // Step 1: Start consultation call
      let customStartCallOptions = JSON.parse(JSON.stringify(startCallOptions));
      customStartCallOptions?.customContext?.voipHeaders?.push({
        key: "unaricParentCallIdInTransfer",
        value: call.id,
      });
      customStartCallOptions?.customContext?.voipHeaders?.push(
        {
          key: "unaricCallerDisplayName",
          value: accountInfo.name,
        },
        {
          key: "unaricCallerUpn",
          value: accountInfo.upn,
        },
        {
          key: "unaricCallerNumber",
          value: call?.callHandlerId || null,
        },
      );
      const consultationCall = await teamsCallAgent.startCall([targetUser], customStartCallOptions);
      /*, {
        videoOptions: { localVideoStreams: [] },
      }*/ //else error MediaAcknowledgment][u27]failed=reject failed, operation not found

      let startCtTime = Date.now();
      let ctCallDir = "Transfer";
      let ctCallEntry = {
        parentCallId: call.id,
        acsCall: consultationCall,
        direction: ctCallDir,
        number: forwardToPhone || null,
        name: tmp.length > 3 && tmp[3] ? tmp[3] : null,
        callerInfo: {
          direction: ctCallDir,
          callerNumber: forwardToPhone || null,
          callerName: tmp.length > 3 && tmp[3] ? tmp[3] : null,
          matchedRecord: null,
        },
        startedAt: startCtTime,
        startedAtUtc: new Date(startCtTime).toISOString().split(".")[0] + "Z",
        startedAtDateTimeStr: getDateTime(startCtTime),
      };
      upsertTransferCallMap(consultationCall.id, ctCallEntry);
      upsertCallMap(call.id, {
        transferredCall: consultationCall.id,
        forwardToPhone,
        forwardToUPN,
        transferTargetInfo: str,
      });
      consultationCall.on("stateChanged", () => {
        console.log("Consultation call state:", consultationCall.state);
        //Connecting -> Ringing -> Connected -....-> Disconnected
        //if agent 2 rejects -> we get connected -> agent 1 can hear Agent2 is unavailable and he can leave msg for voicemail -> Agent1 can either click cancel transfer from ui / wait for sdk to return disconnected state
        let currcall = callMapRef?.current?.get(call.id);
        if (!currcall || (currcall && !currcall.transferType)) {
          if (!currcall.transferredCall) {
            consultationCall.hangUp();
          }
          cancelTransfer(currcall);
        } else {
          if (
            consultationCall.state !== "Disconnecting" &&
            consultationCall.state !== "Disconnected"
          ) {
            upsertTransferCallMap(consultationCall.id, {
              callState: consultationCall.state,
            });
          }
          upsertCallMap(call.id, { callTransferState: consultationCall.state });
        }
        if (consultationCall.state === "Connected") {
          //upsertCallMap(call.id, { transferredCall: consultationCall, callTransferState: consultationCall.state});
        } else if (
          consultationCall.state === "Disconnecting" ||
          consultationCall.state === "Disconnected"
        ) {
          cancelTransfer(currcall, null, true);
        }
      });
    } catch (err) {
      console.error("Error during transfer:", err);
      try {
        cancelTransfer(call);
        console.log("exception : CT failed, but Agent 1 disconnected immediately");
      } catch (er) {
        console.error("exception : during Cancel transfer after CT failed:", er);
      }
    }
  };

  const mute = (call) => {
    if (isEmptyObject(call)) return;
    let childLeg = getTransferredCall(call);
    muteCall(call, upsertCallMap, upsertTransferCallMap, childLeg);
  };

  const unmute = (call) => {
    if (isEmptyObject(call)) return;
    let childLeg = getTransferredCall(call);
    unmuteCall(call, upsertCallMap, upsertTransferCallMap, childLeg);
  };

  const onHold = async (call) => {
    if (isEmptyObject(call)) return;
    await switchFromWrapupCallToOther(call);
    await holdCall(call, upsertCallMap, upsertTransferCallMap);
  };

  const onResume = async (call) => {
    if (isEmptyObject(call)) return;
    //START: hold ongoing call
    let isOtherCall = false,
      isOtherCallOnResume = false;
    let ongoingConnectedCallToHold = null;
    const previousDraftSnapshot = {
      notes: notesRef.current || "",
      callResult: callResultRef.current || "",
      callResultGroup: callResultGroupRef.current || "",
      subject: subjectRef.current || "",
      activeCallId: activeConnectedCallIdRef?.current || null,
    };

    try {
      await switchFromWrapupCallToOther(call);
      if (
        callMapRef?.current?.size > 0 &&
        activeConnectedCallIdRef?.current &&
        activeConnectedCallIdRef.current != call.id
      ) {
        ongoingConnectedCallToHold = callMapRef?.current?.get(activeConnectedCallIdRef?.current);
        if (ongoingConnectedCallToHold && ongoingConnectedCallToHold?.transferredCall != call.id) {
          //else if we click resume/ hold on transfer leg, the notes disappear as its considered a 2nd call
          isOtherCall = true;
          if (!ongoingConnectedCallToHold.isHold && !ongoingConnectedCallToHold.isWrapup) {
            isOtherCallOnResume = true;
          }
        }
      }

      if (isOtherCall) {
        setIsSwitchingCall(true);
      }

      if (isOtherCall && !wrapupConnectedCallIdRef?.current) {
        persistDraftForCall(activeConnectedCallIdRef?.current);
        clearDraftState();
      }
      if (isOtherCallOnResume) {
        await onHold(ongoingConnectedCallToHold);
      }
      let parentCall = call?.parentCallId ? callMapRef?.current?.get(call?.parentCallId) : null;
      let childCall = call?.transferredCall
        ? transferCallMapRef?.current?.get(call?.transferredCall)
        : null;
      await resumeCall(call, upsertCallMap, upsertTransferCallMap, parentCall, childCall);
      if (isOtherCall) {
        applyDraftState(call, call.id);
      }
    } catch (error) {
      console.error("Failed to switch active call:", error);
      setNotes(previousDraftSnapshot.notes);
      setCallResult(previousDraftSnapshot.callResult);
      setCallResultGroup(previousDraftSnapshot.callResultGroup);
      setSubject(previousDraftSnapshot.subject);
      if (previousDraftSnapshot.activeCallId) {
        setActiveConnectedCallId(previousDraftSnapshot.activeCallId);
      }
      setErrorMessage("Failed to switch call.");
    } finally {
      if (isOtherCall) {
        setIsSwitchingCall(false);
      }
    }
    //END: hold ongoing call
  };

  const switchFromWrapupCallToOther = async (call) => {
    if (wrapupConnectedCallIdRef?.current) {
      let ongoingConnectedWrapupCall = callMapRef?.current?.get(wrapupConnectedCallIdRef.current);
      if (ongoingConnectedWrapupCall && ongoingConnectedWrapupCall.isWrapupInEditMode) {
        if (ongoingConnectedWrapupCall.isWrapupInEditMode) {
          persistDraftForCall(ongoingConnectedWrapupCall.id, {
            isWrapupInEditMode: false,
          });
          applyDraftState(call, call?.id);
        }
      }
    }
  };

  const handleDtmf = (digit, call) => {
    if (isEmptyObject(call.acsCall)) return;
    let childLeg = getTransferredCall(call);
    if (!isEmptyObject(childLeg) && !childLeg?.isHold) {
      //only when child leg is there and in resumed mode, unmute it else unmute parent call irrespective of hold state
      sendDtmfTone(childLeg.acsCall, digit);
    } else {
      sendDtmfTone(call.acsCall, digit);
    }
  };

  const subscribeToRemoteParticipant = (remoteParticipant) => {
    console.log(`Remote participant state: ${remoteParticipant.state}`);

    remoteParticipant.on("stateChanged", () => {
      console.log(`Remote participant state changed: ${remoteParticipant.state}`);
    });
  };

  const placeCallInternal = async (phoneNumber, agent, devManager, clickToDialObj) => {
    try {
      let startTime = Date.now();
      if (startTime - lastDialTime < 3000) {
        console.log("Dial denied! Too many dials in the interval of 1 second.");
      } else {
        // Stop previous synthetic tones before a new outbound attempt starts.
        outboundCallToneControllerRef.current?.stopAllTones();
        lastDialTime = startTime;
        let hvsCallId = hvsWorkId.current;
        hvsWorkId.current = null;
        setSoftphonePanelVisibility(true);

        if (clickToDialObj?.params) {
          clickToDialObj.params = Object.fromEntries(
            clickToDialObj.params.split(",").map((pair) => pair.split("=").map(decodeURIComponent)),
          );
        }

        if (callMapRef?.current?.size > 0 && clickToDialObj?.params?.terminateExisting === "true") {
          const draftCallId = activeConnectedCallIdRef?.current;
          const shouldPersistActiveDraft =
            !!draftCallId &&
            callMapRef.current.has(draftCallId) &&
            wrapupConnectedCallIdRef.current !== draftCallId;
          if (shouldPersistActiveDraft) {
            persistDraftForCall(draftCallId);
          }
          const callIds = Array.from(callMapRef.current.keys());
          for (const callId of callIds) {
            if (callMapRef.current.get(callId).acsCall.state !== "Disconnected") {
              suppressWrapupUiCallIdsRef.current.add(callId);
              await hangUp(callId);
            }
          }
          if (shouldPersistActiveDraft) {
            clearDraftState();
          }
        } else if (callMapRef?.current?.size > 0 && activeConnectedCallIdRef?.current) {
          //hold ongoing in/out call
          let ongoingConnectedCallToHold = callMapRef?.current?.get(
            activeConnectedCallIdRef.current,
          );
          if (
            ongoingConnectedCallToHold &&
            !ongoingConnectedCallToHold.isHold &&
            !ongoingConnectedCallToHold.isWrapup
          ) {
            persistDraftForCall(activeConnectedCallIdRef.current);
            clearDraftState();
            await onHold(ongoingConnectedCallToHold);
          }
        }

        // for setting the lastEntityIdRef to null beacause this value is not cleared in unsanswered case
        lastEntityIdRef.current = null;
        assignMetaRef.current = {
          recordId: null,
          recordName: null,
          objectType: null,
        };
        setDeletedAssignedRecId(null);

        let numToDial = phoneNumber;
        let isInternal = false;
        if (numToDial?.indexOf("@") == -1) {
          const cleanNumber = phoneNumber?.replace(/[\s-()]/g, "");
          // Strict E.164: +[country][subscriber], 7-15 digits total, leading digit 1-9
          //const e164 = cleanNumber;

          numToDial = cleanNumber;
        } else {
          isInternal = true;
        }
        /*cleanNumber.startsWith("+") ? cleanNumber : `+${cleanNumber}`;
        const isValidE164 = /^\+[1-9]\d{6,14}$/.test(e164);
        if (!isValidE164) {
          setErrorMessage("Enter a valid E.164 number, e.g. +15551234567");
          return;
        }*/ //Commented to be able to place an emergency call

        if (isTranslate911Enabled() && (numToDial === "911" || numToDial === "933")) {
          const translatedNum =
            numToDial === "911" ? getTranslated911Number() : getTranslated933Number();
          const translatedNumSafe = typeof translatedNum === "string" ? translatedNum : "";
          const cleanTranslatedNumber = translatedNumSafe?.replace(/[\s-()]/g, ""); //removes all spaces, hyphens, and parentheses
          numToDial = cleanTranslatedNumber ? cleanTranslatedNumber : numToDial;
        }
        let callTarget = null;
        if (isInternal) {
          if (clickToDialObj?.params?.graphUserId) {
            callTarget = {
              microsoftTeamsUserId: clickToDialObj.params.graphUserId,
              cloud: "public",
            };
          } else {
            setErrorMessage("Failed to start the call. Please try again.");
            return;
          }
        } else {
          callTarget = { phoneNumber: numToDial };
        }

        let callerGraphUserId = null;
        if (isInternal && callTarget.microsoftTeamsUserId) {
          callerGraphUserId = callTarget.microsoftTeamsUserId;
        }

        let customStartCallOptions = null;
        customStartCallOptions = JSON.parse(JSON.stringify(startCallOptions));
        customStartCallOptions?.customContext?.voipHeaders?.push(
          {
            key: "unaricCallerDisplayName",
            value: accountInfo.name,
          },
          {
            key: "unaricCallerUpn",
            value: accountInfo.upn,
          },
          {
            key: "unaricCallerNumber",
            value: call?.callHandlerId || null,
          },
        );
        if (isInternal && callTarget.microsoftTeamsUserId) {
          customStartCallOptions?.customContext?.voipHeaders?.push({
            key: "unaricIsInternalCall",
            value: true,
          });
        }

        const newCall = agent.startCall(callTarget, customStartCallOptions);
        let callerInfoTmp = {
          direction: "Outbound",
          callerNumber: !isInternal ? numToDial : null, //For subject in internal/ transfer calls to show display name instead of upn
          callerName: null,
          matchedRecord: null,
        };

        if (clickToDialObj && clickToDialObj.recordName && isInternal) {
          callerInfoTmp.callerName = clickToDialObj?.recordName ?? null;
        }

        upsertCallMap(newCall.id, {
          acsCall: newCall,
          callState: "Connecting",
          direction: "Outbound",
          hvsCallId:
            hvsCallId != undefined && hvsCallId != "" && hvsCallId != "NO_WORK_ID"
              ? hvsCallId
              : null,
          number: numToDial,
          name: isInternal && clickToDialObj?.recordName ? clickToDialObj.recordName : null,
          callerGraphUserId,
          startedAt: startTime,
          startedAtUtc: new Date(startTime).toISOString().split(".")[0] + "Z",
          startedAtDateTimeStr: getDateTime(startTime),
          callerInfo: callerInfoTmp,
          callFromPhone: selfPhoneNumber(newCall),
          callToPhone: !isInternal ? numToDial : null,
          callToUPN: isInternal ? numToDial : null,
          callHandlerId: selfPhoneNumber(newCall),
        });
        setActiveConnectedCallId(newCall.id);
        outboundNumSearch(numToDial, clickToDialObj, newCall);
        await switchFromWrapupCallToOther(newCall);
        subscribeToCall(newCall);
        setErrorMessage("");
        if (typeof sforce !== "undefined") {
          sforce.opencti.setSoftphoneItemIcon({ key: "outbound_call" });
        }
      }
    } catch (error) {
      console.error("Failed to place call:", error);
      const msg = String(error?.message || "").toLowerCase();
      if (msg.includes("permission") || msg.includes("pstn")) {
        setErrorMessage("Your account does not have PSTN calling enabled.");
      } else {
        setErrorMessage("Failed to start the call. Please try again.");
      }
    }
  };

  const placeCall = useCallback(async (phoneNumber, clickToDialObj) => {
    if (!teamsCallAgent || !deviceManager) {
      console.error("Client not initialized");
      return;
    }
    await placeCallInternal(phoneNumber, teamsCallAgent, deviceManager, clickToDialObj);
  });

  const outScreenPopHandler = (doPop, callId, numToDial, matchedRecordIds) => {
    if (doPop) {
      const flowToPop = getOutboundScreenPopFlow();
      if (flowToPop) {
        let popParams = {
          callId,
          number: numToDial,
          name: "",
          callType: "Outbound",
        };
        if (numToDial.indexOf("@") == -1) {
          popParams.source = "pstn";
        } else {
          popParams.source = "teams";
        }
        //popParams.number = encodeURIComponent(numToDial);
        popParams = generateFlowParams(popParams)?.FLOW?.flowArgs ?? [];
        let popData = {
          type: sforce.opencti.SCREENPOP_TYPE.FLOW,
          params: { flowDevName: flowToPop, flowArgs: popParams },
        };
        popData = enrichScreenPopData(popData, matchedRecordIds);
        screenPop(popData);
      } else {
        //fallback
        if (matchedRecordIds.length > 1) {
          //Multiple -> always open search page
          //popData.params.flowArgs.push({ name: "recordIds", type: "String", value: matchedRecordIds.join(",") });
          screenPop({ type: "SEARCH", params: { searchString: numToDial } });
        } else if (matchedRecordIds.length === 1) {
          //Single -> always open detail page
          screenPop({
            type: "SOBJECT",
            params: { recordId: matchedRecordIds[0] },
          });
        } else {
          //No match
          //do nothing
        }
        /*if (matchedRecordIds.length === 1) { //Single -> always open detail page
          screenPop({type: "SOBJECT", params: { recordId : matchedRecordIds[0] }});
        } else {
          if(isMul) { //Multiple -> always open search page
            screenPop({type: "SEARCH", params: {searchString: numToDial}});
          } else { //No match
            //do nothing
          }
        }*/
      }
    }
  };

  const fallbackAssignment = (recordId, recordObj, srchRes) => {
    if (recordId.substring(0, 3) == "003" || recordId.substring(0, 3) == "00Q") {
      srchRes.whoId = recordId;
      srchRes.whoName = recordObj.Name || recordObj.recordName || "";
      srchRes.whoType = recordObj.RecordType || recordObj.objectType || null;
    } else {
      srchRes.whatId = recordId;
      srchRes.whatName = recordObj.Name || recordObj.recordName || "";
      srchRes.whatType = recordObj.RecordType || recordObj.objectType || null;
    }
    return srchRes;
  };

  const refineAssignment = (doPop, callId, numToDial, matchedRecordIds, outCallMapData) => {
    const whatIdPrefixesDontSave = ["00v"];
    const donotAssignPrefixesToTask = ["00T", "00U", "02s", "01Z", "00O", "00l"]; //02s = EmailMessage, 01Z -> Dashboard, 00O -> Report, 00l -> Folder
    const actEnabledWhatIdPrefixes = getWhatIdListOnTask();
    const whatIdPrefix = outCallMapData?.whatId?.substring(0, 3);

    if (
      whatIdPrefix &&
      (donotAssignPrefixesToTask.includes(whatIdPrefix) ||
        whatIdPrefixesDontSave.includes(whatIdPrefix) ||
        (actEnabledWhatIdPrefixes.length > 0 && !actEnabledWhatIdPrefixes.includes(whatIdPrefix)))
    ) {
      if (
        outCallMapData?.callerInfo?.matchedRecord?.recordId?.substring(0, 15) ==
        outCallMapData.whatId.substring(0, 15)
      ) {
        outCallMapData.callerInfo.callerName = null;
        outCallMapData.callerInfo.matchedRecord.recordId = "";
        outCallMapData.callerInfo.matchedRecord.name = "";
        outCallMapData.callerInfo.matchedRecord.recordType = null;

        matchedRecordIds = matchedRecordIds.filter(
          (id) => id.substring(0, 15) !== outCallMapData.whatId.substring(0, 15),
        );
      }

      outCallMapData.whatId = "";
      outCallMapData.whatName = "";
      outCallMapData.whatType = "";
    }
    upsertCallMap(callId, outCallMapData);
    outScreenPopHandler(doPop, callId, numToDial, matchedRecordIds);
  };

  const outboundNumSearch = (numToDial, clickToDialObj, newCall) => {
    let doPop = false;
    if (clickToDialObj) {
      //can have record linked or not like from recent tab
      doPop = isOutboundScreenPopForClickToDialEnabled();
    } else {
      //manual dial
      doPop = isOutboundScreenPopForDialPadEnabled();
    }

    let outCallMapData = {};
    let callerInfoTmp = {
      direction: "Outbound",
      callerNumber: numToDial,
      callerName: null,
      matchedRecord: null,
    };

    if (
      clickToDialObj &&
      clickToDialObj.recordId &&
      clickToDialObj.recordName &&
      sforce &&
      sforce.opencti
    ) {
      //&& !(donotAssignPrefixesToTask.includes(clickToDialObj.recordId.substring(0, 3)))
      callerInfoTmp.callerName = clickToDialObj?.recordName ?? null;
      callerInfoTmp.matchedRecord = {
        recordId: clickToDialObj.recordId,
        name: clickToDialObj.recordName || "",
        recordType: clickToDialObj.objectType || null,
      };
      outCallMapData.callerInfo = callerInfoTmp;

      if (
        sforce &&
        sforce.opencti &&
        (clickToDialObj.recordId.substring(0, 3) == "003" ||
          clickToDialObj.recordId.substring(0, 3) == "500" ||
          clickToDialObj.recordId.substring(0, 3) == "00T" ||
          clickToDialObj.recordId.substring(0, 3) == "00U" ||
          clickToDialObj.recordId.substring(0, 3) == "00v")
      ) {
        //00v = campaign member
        try {
          sforce.opencti.runApex({
            apexClass: "unrc.UnaricUnifyClientCtiController",
            methodName: "getRelatedRecordInfo",
            methodParams:
              "recordId=" +
              clickToDialObj.recordId +
              "&recordName=" +
              clickToDialObj.recordName +
              "&objectType=" +
              clickToDialObj.objectType,
            callback: (result) => {
              if (result?.success && result?.returnValue?.runApex) {
                const wrapperJson = result.returnValue.runApex;
                const wrapper = wrapperJson ? JSON.parse(wrapperJson) : null;

                if (wrapper && (wrapper.whoId || wrapper.whatId)) {
                  outCallMapData.whoId = wrapper.whoId;
                  outCallMapData.whoName = wrapper.whoName;
                  outCallMapData.whoType = wrapper.whoType;
                  outCallMapData.whatId = wrapper.whatId;
                  outCallMapData.whatName = wrapper.whatName;
                  outCallMapData.whatType = wrapper.whatType;

                  //e.x. replace task with contact
                  const extMatchedRecordId =
                    outCallMapData?.callerInfo?.matchedRecord?.recordId?.substring(0, 15);
                  const extWhoId = outCallMapData?.whoId?.substring(0, 15);
                  const extWhatId = outCallMapData?.whatId?.substring(0, 15);

                  if (extMatchedRecordId !== extWhoId && extMatchedRecordId !== extWhatId) {
                    const newData = outCallMapData?.whoId
                      ? {
                          id: outCallMapData.whoId,
                          name: outCallMapData?.whoName,
                          type: outCallMapData?.whoType,
                        }
                      : outCallMapData?.whatId
                        ? {
                            id: outCallMapData.whatId,
                            name: outCallMapData?.whatName,
                            type: outCallMapData?.whatType,
                          }
                        : { id: "", name: "", type: null };

                    outCallMapData.callerInfo.callerName = newData.name ?? null;
                    outCallMapData.callerInfo.matchedRecord = {
                      recordId: newData.id,
                      name: newData.name || "",
                      recordType: newData.type || null,
                    };
                    refineAssignment(doPop, newCall.id, numToDial, [newData.id], outCallMapData);
                  } else {
                    refineAssignment(
                      doPop,
                      newCall.id,
                      numToDial,
                      [clickToDialObj.recordId],
                      outCallMapData,
                    );
                  }
                } else {
                  outCallMapData = fallbackAssignment(
                    clickToDialObj.recordId,
                    clickToDialObj,
                    outCallMapData,
                  );
                  refineAssignment(
                    doPop,
                    newCall.id,
                    numToDial,
                    [clickToDialObj.recordId],
                    outCallMapData,
                  );
                }
              } else {
                console.error("Error calling Apex getRelatedRecordInfo:", result?.errors);
                outCallMapData = fallbackAssignment(
                  clickToDialObj.recordId,
                  clickToDialObj,
                  outCallMapData,
                );
                refineAssignment(
                  doPop,
                  newCall.id,
                  numToDial,
                  [clickToDialObj.recordId],
                  outCallMapData,
                );
              }
            },
          });
        } catch (ex) {
          console.warn("call to apex getRelatedRecordInfo Failed :", ex);
          outCallMapData = fallbackAssignment(
            clickToDialObj.recordId,
            clickToDialObj,
            outCallMapData,
          );
          refineAssignment(doPop, newCall.id, numToDial, [clickToDialObj.recordId], outCallMapData);
        }
      } else {
        outCallMapData = fallbackAssignment(
          clickToDialObj.recordId,
          clickToDialObj,
          outCallMapData,
        );
        refineAssignment(doPop, newCall.id, numToDial, [clickToDialObj.recordId], outCallMapData);
      }
    } else if (numToDial?.indexOf("@") == -1) {
      //manual dial/ click to dial from recent tab with no record linked
      // ---- Salesforce screen pop (best effort) ----
      if (typeof sforce !== "undefined") {
        try {
          searchAndScreenPop({
            searchParams: numToDial,
            callType: sforce.opencti.CALL_TYPE.OUTBOUND,
            popParams: {
              number: numToDial,
              source: "pstn",
              callType: "Outbound",
            },
            shouldPop: false,
            onSuccess: ({ recordId, record, matchedRecordIds }) => {
              //with who/whatid only = single match/ mul with single account
              console.log("success found", record);

              callerInfoTmp.callerName = record?.whoName || record?.whatName || null;
              callerInfoTmp.matchedRecord = {
                recordId: record?.whoId || record?.whatId || recordId || null,
                name: record?.whoName || record?.whatName || null,
                recordType: record?.whoType || record?.whatType || null,
              };
              outCallMapData.callerInfo = callerInfoTmp;
              outCallMapData = { ...outCallMapData, ...record }; //get who/ whatid
              refineAssignment(doPop, newCall.id, numToDial, matchedRecordIds, outCallMapData);
            },
            onNoMatch: ({ matchedRecordIds }) => {
              //no match/ multiple match with no single account
              outCallMapData.callerInfo = callerInfoTmp;
              refineAssignment(doPop, newCall.id, numToDial, matchedRecordIds, outCallMapData);
            },
          });
        } catch (sfErr) {
          console.warn("Salesforce screen pop failed:", sfErr);
        }
      }
    }
  };

  const getActiveCall = async () => {
    console.log("click ongetActiveCall");
    if (teamsCallAgent) {
      const activeCallTransferFeature = teamsCallAgent.feature(Features.ActiveCallTransfer);
      const activeCallDetails = await activeCallTransferFeature.getActiveCallDetails();
      console.log("activeCallDetails fun", activeCallDetails);
    }
  };

  const switchActiveCall = async (isTransfer = true) => {
    /*if (!teamsCallAgent) return;
    try {
      const joinCallOptions = {
        audioOptions: { isMuted: false }
      }
      const options = isTransfer ? { isTransfer: true, joinCallOptions } : { isTransfer: false }
      const activeCallTransferFeature = teamsCallAgent.feature(Features.ActiveCallTransfer);
      const activeCallDetails = await activeCallTransferFeature.getActiveCallDetails();
      const callSwitch = await activeCallTransferFeature.activeCallTransfer(activeCallDetails[0], options);

      console.log("switchActiveCall", callSwitch);
      if (callSwitch && !isTransfer) {
        await callSwitch.mute();
        //setIsMuted(true);
        upsertCallMap(callSwitch.id, {
          acsCall: callSwitch, 
          answeredAt: (activeCallDetails?.[0]?.userJoinTime.getTime()),
          isMuted : true
        });
      }
      // call recording
      // notifyCallConnected(callSwitch)
      // call recording
      const rawNumber = activeCallDetails?.[0]?.participants?.[0]?.phoneNumber ?? null
      //callStartTimeRef.current=activeCallDetails?.[0]?.userJoinTime.getTime();
      if (!rawNumber) {
        console.warn("No phone number found, skipping Salesforce log");
        // return;
      }
      // Normalize E.164
      const digits = String(rawNumber).replace(/[^\d+]/g, "");
      const e164 = digits.startsWith("+") ? digits : `+${digits}`;
      let resolvedName = "";

      // ---- Salesforce screen pop (best effort) ----
      if (typeof sforce !== "undefined") {
        try {
          searchAndScreenPopOnly({
            searchParams: e164,
            callType: sforce.opencti.CALL_TYPE.INBOUND,
            queryParams: `name=&number=${encodeURIComponent(
              e164
            )}&source=pstn`,
            onSuccess: ({ record }) => {
              resolvedName = record?.Name ?? "";
            },
          });
        } catch (sfErr) {
          console.warn("Salesforce screen pop failed:", sfErr);
        }
      }
      let inboundMeta = {
        callerNumber: rawNumber,
        callerName: resolvedName,
        matchedRecord: null,  // will be filled after searchAndScreenPop result
        direction:callSwitch?.direction === "Outgoing" ? "Outbound" : "Inbound",
      };
      //lastCallMetaRef.current = {
      //  direction: callSwitch?.direction === "Outgoing" ? "Outbound" : "Inbound",
      //  number: rawNumber,
      //  name: resolvedName,
      //};
      //setCallerInfo(inboundMeta); // initial state
      //setCall(callSwitch);
      upsertCallMap(callSwitch.id, { 
        //acsCall: callSwitch, 
        //answeredAt: (activeCallDetails?.[0]?.userJoinTime.getTime()),
        callerInfo: inboundMeta,
        direction: (callSwitch?.direction === "Outgoing" ? "Outbound" : "Inbound"),
        number: rawNumber,
        name: resolvedName
      });
      subscribeToCall(callSwitch);
      setErrorMessage("");
    } catch (error) {
      console.error("Failed to accept call:", error);
      setErrorMessage("Failed to Switch the call.");
    }*/
    //Need to uncomment in beta 2
  };

  const acceptCall = async (callId) => {
    const acceptTs = Date.now();
    const acsCall = incomingCallMapRef?.current?.get(callId)?.acsCall;
    if (isEmptyObject(acsCall)) return;

    try {
      const acceptedCall = await acsCall.accept();
      console.log("acceptedCall", acceptedCall);
      setSoftphoneItemIcon("call");
      //hold ongoing in/out call
      let putExistCallOnHold = false;
      let ongoingConnectedCall = null;
      const existingObjFromIncomingCallMap = incomingCallMapRef.current.get(acceptedCall.id);
      if (
        existingObjFromIncomingCallMap &&
        callMapRef?.current?.size > 0 &&
        activeConnectedCallIdRef?.current
      ) {
        ongoingConnectedCall = callMapRef?.current?.get(activeConnectedCallIdRef.current);
        if (
          ongoingConnectedCall &&
          !ongoingConnectedCall.isHold &&
          !ongoingConnectedCall.isWrapup
        ) {
          putExistCallOnHold = true;
        }
      }

      if (existingObjFromIncomingCallMap.screenPopData) {
        screenPop(existingObjFromIncomingCallMap.screenPopData);
      }

      if (ongoingConnectedCall) {
        let upsertMap = {
          notes: notesRef.current || "",
          subject: subjectRef.current || "",
          callResult: callResultRef.current || "",
          callResultGroup: callResultGroupRef.current || "",
        };
        if (wrapupConnectedCallIdRef?.current) {
          let ongoingConnectedWrapupCall = callMapRef?.current?.get(
            wrapupConnectedCallIdRef.current,
          );
          if (ongoingConnectedWrapupCall && ongoingConnectedWrapupCall.isWrapupInEditMode) {
            upsertMap.isWrapupInEditMode = false;
          }
        }
        persistDraftForCall(activeConnectedCallIdRef.current, upsertMap);
        applyDraftState({});
      }
      if (putExistCallOnHold) {
        onHold(ongoingConnectedCall); //await onHold(ongoingConnectedCall); //so that it immediately moves to Phonetab
        setActiveConnectedCallId(acceptedCall.id);
        //omit acsCall else hangup does not work
        const { acsCall, ...rest } = existingObjFromIncomingCallMap;
        const movedCallObj = {
          ...rest,
          acsCall: acceptedCall,
          answeredAt: acceptTs, //with new changes of hold, if done on subscribe, its not called
        };
        if (showEditSubject) {
          let subNew = createSubjectForCall(movedCallObj);
          movedCallObj.subject = subNew;
          setSubject(subNew || "");
        }
        upsertCallMap(acceptedCall.id, movedCallObj);
        removeFromIncomingCallMap(acceptedCall.id);
        subscribeToCall(acceptedCall);
        setErrorMessage("");
      } else {
        setActiveConnectedCallId(acceptedCall.id);
        //omit acsCall else hangup does not work
        const { acsCall, ...rest } = existingObjFromIncomingCallMap;
        const movedCallObj = {
          ...rest,
          acsCall: acceptedCall,
          //answeredAt: Date.now() //done on subscribe
        };
        if (showEditSubject) {
          let subNew = createSubjectForCall(movedCallObj);
          movedCallObj.subject = subNew;
          setSubject(subNew || "");
        }
        upsertCallMap(acceptedCall.id, movedCallObj);
        removeFromIncomingCallMap(acceptedCall.id);
        subscribeToCall(acceptedCall);
        setErrorMessage("");
      }
    } catch (error) {
      console.error("Failed to accept call:", error);
      setErrorMessage("Failed to accept the call.");
    }
  };

  const hangUp = async (callId) => {
    setonAnotherDevice(false);

    let callToHang = incomingCallMapRef?.current?.get(callId);
    if (!isEmptyObject(callToHang)) {
      if (callToHang?.acsCall) {
        //removed from map in incoming.js else for hang via btn task was not logged
        await callToHang.acsCall.reject();
      } else {
        //call is just on ui so reject would fail
        removeFromIncomingCallMap(callToHang.id);
      }
    }

    callToHang = callMapRef?.current?.get(callId);
    if (isEmptyObject(callToHang)) {
      callToHang = transferCallMapRef?.current?.get(callId);
      if (isEmptyObject(callToHang)) return;
      return cancelTransfer(null, callToHang);
    }

    try {
      try {
        if (callToHang?.acsCall) {
          await callToHang.acsCall.hangUp();
        } else {
          finalizeAndRemoveCall(callToHang.id);
        }
      } catch (err) {
        console.error("sdk failed to hang up:", err);
      }
      setErrorMessage("");
    } catch (error) {
      console.error("Failed to hang up:", error);
      setErrorMessage("Failed to hang up.");
    }
  };

  const wrapupEdit = async (ongoingWrapupCall) => {
    try {
      if (ongoingWrapupCall.isWrapupInEditMode) {
        //do nothing - could be this is the call which was active and was just ended.
      } else {
        //store wrapup for ongoing call in map
        if (activeConnectedCallIdRef?.current) {
          persistDraftForCall(activeConnectedCallIdRef?.current);
          applyDraftState({});
        }

        //fetch wrapup for ended call from map and show on ui, do not set it as active
        upsertCallMap(ongoingWrapupCall.id, { isWrapupInEditMode: true });
        applyDraftState(ongoingWrapupCall, ongoingWrapupCall.id);
      }
    } catch (error) {
      console.error("Failed to allow editiing of call in Wrap-Up mode : ", error);
      setErrorMessage("Failed to allow editiing of call in Wrap-Up mode.");
    }
  };

  //wait until ongoingWrapupCall.activityId is set
  const waitForActivityId = (ongoingWrapupCall, timeoutMs = 5000) => {
    return new Promise((resolve) => {
      const start = Date.now();
      const check = () => {
        ongoingWrapupCall = callMapRef?.current?.get(ongoingWrapupCall.id);
        if (ongoingWrapupCall?.activityId) {
          resolve(ongoingWrapupCall.activityId);
        } else if (Date.now() - start >= timeoutMs) {
          // timeout reached, resolve with fallback
          resolve("");
        } else {
          setTimeout(check, 5);
        }
      };
      check();
    });
  };

  const getWrapupSaveContext = (wrapupCallId, existingWrapupCall) => {
    const wrapupCall = callMapRef?.current?.get(wrapupCallId) || existingWrapupCall;
    const uiDraft = getDraftValues({
      notesRef,
      callResultRef,
      callResultGroupRef,
      subjectRef,
    });
    const isEditingThisWrapupCall =
      !!wrapupCall?.isWrapupInEditMode && activeConnectedCallIdRef?.current === wrapupCallId;
    const wrapupPayload = getWrapupSavePayload({
      wrapupCall,
      isEditingThisWrapupCall,
      uiDraft,
    });

    return {
      wrapupCall,
      uiDraft,
      isEditingThisWrapupCall,
      wrapupPayload,
    };
  };

  const transitionUiAfterWrapup = ({ wrapupCallId, isOtherCall, uiDraft }) => {
    if (callMapRef?.current?.size === 1) {
      clearDraftState();
      return;
    }

    if (!isOtherCall) {
      const entry = getNextVisibleEntryAfterWrapup(wrapupCallId);
      if (!entry) {
        return;
      }

      const [entryCallId] = entry;
      if (entryCallId === activeConnectedCallIdRef?.current) {
        applyDraftState(uiDraft, entryCallId);
      } else {
        showSelectedEntry(entry);
      }
      return;
    }

    return;
  };

  const getRingDurTotalInSec = (meta) => {
    const startedAtTs = meta?.startedAt;
    const ansAtTs = meta?.answeredAt;
    const ringDurationSec =
      startedAtTs && ansAtTs ? Math.max(0, Math.round((ansAtTs - startedAtTs) / 1000)) : 0;

    return ringDurationSec;
  };

  const getHoldDurTotalInSec = (meta, endTs) => {
    endTs = endTs ? endTs : meta.endedAt;
    let holdDurationSec = 0;
    let holdDurTotalTs = meta?.holdDurationTotal;
    if (meta?.isHold && meta?.holdedAt) {
      //call getting ended in hold state
      holdDurTotalTs += endTs - meta?.holdedAt;
    }
    holdDurationSec = holdDurTotalTs ? Math.max(0, Math.round(holdDurTotalTs / 1000)) : 0;
    return holdDurationSec;
  };

  const getAnsDurTotalInSec = (meta, endTs) => {
    endTs = endTs ? endTs : meta.endedAt;
    let durationSec = 0;
    let ansAtTs = meta?.answeredAt;
    durationSec = ansAtTs ? Math.max(0, Math.round((endTs - ansAtTs) / 1000)) : 0;

    return durationSec;
  };

  const saveTask = (
    meta,
    isRetry,
    callResultForActiveCall,
    callResultGroupForActiveCall,
    notesForActiveCall,
    subjectForActiveCall,
    endTs,
    endedAtUtc,
  ) => {
    endedAtUtc = endedAtUtc ? endedAtUtc : meta.endedAtUtc;
    const ringDurationSec = getRingDurTotalInSec(meta);
    const durationSec = getAnsDurTotalInSec(meta, endTs);
    const holdDurationSec = getHoldDurTotalInSec(meta, endTs);

    logSalesforceActivity({
      upsertCallMap,
      direction: meta?.direction,
      number: meta?.callerInfo?.callerNumber,
      name: meta?.callerInfo?.matchedRecord?.name || meta?.callerInfo?.callerName || null,
      matchedRecordId: meta?.callerInfo?.matchedRecord?.recordId,
      whoId: meta?.whoId,
      whoName: meta?.whoName,
      whoType: meta?.whoType,
      whatId: meta?.whatId,
      whatName: meta?.whatName,
      whatType: meta?.whatType,
      hvsCallId: meta?.hvsCallId,
      callId: meta.id,
      startedAt: meta.startedAt,
      startedAtDateTimeStr: meta.startedAtDateTimeStr,
      startedAtUtc: meta.startedAtUtc,
      endedAtUtc,
      durationSec,
      ringDurationSec,
      holdDurationSec,
      callResult: callResultForActiveCall,
      callResultGroup: callResultGroupForActiveCall,
      notes: notesForActiveCall,
      subject: subjectForActiveCall,
      queueName:
        meta?.transferorInfo?.callScenario === "CallQueue" && meta?.transferorInfo?.displayName
          ? meta?.transferorInfo?.displayName
          : "", //Agent 1/2
      transferredBy:
        meta?.transferorInfo?.callScenario === "TransferToParticipant" &&
        meta?.transferorInfo?.displayName
          ? meta?.transferorInfo?.displayName
          : "", //Agent 2
      callFromPhone: meta?.callFromPhone, //Inbound = Customer phonenum
      callFromUpn: meta?.callFromUpn, //Inbound = Customer UPN = Other agent's UPN = internal call / transfer leg
      callToPhone: meta?.callToPhone, //Inbound = Agent's number
      callToUPN: meta?.callToUPN,
      callHandlerId: meta?.callHandlerId,
      callerGraphUserId: meta?.callerGraphUserId,
      transferFromUpn: meta?.transferFromUpn,
      forwardToPhone: meta?.forwardToPhone,
      forwardToUPN: meta?.forwardToUPN,
      setErrorMessage,
      isRetry,
    });
  };

  const finalizeWrapup = async ({ wrapupCallId, wrapupCall, wrapupPayload }) => {
    setWrapupConnectedCallId(null);
    const activityId = wrapupCall?.activityId
      ? wrapupCall.activityId
      : await waitForActivityId(wrapupCall, 5000);
    finalizeAndRemoveCall(wrapupCallId);
    if (activityId) {
      updateSalesforceActivity({
        showEditSubject,
        whoId: wrapupCall?.whoId,
        whoName: wrapupCall?.whoName,
        whoType: wrapupCall?.whoType,
        whatId: wrapupCall?.whatId,
        whatName: wrapupCall?.whatName,
        whatType: wrapupCall?.whatType,
        callObject: wrapupCall?.id,
        activityId: activityId || "",
        callResult: wrapupPayload.callResult,
        callResultGroup: wrapupPayload.callResultGroup,
        notes: wrapupPayload.notes,
        subject: wrapupPayload.subject,
        direction: wrapupCall?.direction,
        isAnswered: wrapupCall?.answeredAt,
        number: wrapupCall?.callerInfo?.callerNumber,
        name:
          wrapupCall?.callerInfo?.matchedRecord?.name || wrapupCall?.callerInfo?.callerName || null,
        setErrorMessage,
      });
    } else {
      saveTask(
        wrapupCall,
        true,
        wrapupPayload.callResult,
        wrapupPayload.callResultGroup,
        wrapupPayload.notes,
        wrapupPayload.subject,
      );
    }
  };

  const wrapupDone = async (ongoingWrapupCall, isOtherCall) => {
    const wrapupCallId = ongoingWrapupCall?.id;
    if (!wrapupCallId) return;

    if (wrapupSaveInFlightRef.current.has(wrapupCallId)) {
      return;
    }
    wrapupSaveInFlightRef.current.add(wrapupCallId);

    const existingWrapupCall = callMapRef?.current?.get(wrapupCallId);
    if (!existingWrapupCall || existingWrapupCall?.isWrapupSaving) {
      wrapupSaveInFlightRef.current.delete(wrapupCallId);
      return;
    }

    upsertCallMap(wrapupCallId, { isWrapupSaving: true });

    try {
      const { wrapupCall, uiDraft, isEditingThisWrapupCall, wrapupPayload } = getWrapupSaveContext(
        wrapupCallId,
        existingWrapupCall,
      );

      transitionUiAfterWrapup({
        wrapupCallId,
        isOtherCall,
        uiDraft,
      });
      await finalizeWrapup({
        wrapupCallId,
        wrapupCall,
        wrapupPayload,
      });
    } catch (error) {
      console.error("Failed to complete wrapup: ", error);
      setErrorMessage("Failed to complete wrapup.");
    } finally {
      wrapupSaveInFlightRef.current.delete(wrapupCallId);
      if (callMapRef?.current?.has(wrapupCallId)) {
        upsertCallMap(wrapupCallId, { isWrapupSaving: false });
      }
    }
  };

  const logout = () => {
    clickToDialEnabled.current = false;
    disableClickToDial();
    if (teamsCallAgent) {
      teamsCallAgent.dispose(); //Uncommented so that in logout state, sdk does not process calls
      setTeamsCallAgent(null);
    }
    setDeviceManager(null);
    lastEntityIdRef.current = null;
    assignMetaRef.current = {
      recordId: null,
      recordName: null,
      objectType: null,
    };
    setDeletedAssignedRecId(null);
    setCallMap(() => new Map());
    setIncomingCallMap(() => new Map());
    setTransferCallMap(() => new Map());
  };

  const setNotes = (text) => {
    _setNotes(text);
    notesRef.current = text || "";
  };

  const setCallResult = (text) => {
    _setCallResult(text);
    callResultRef.current = text || "";
  };

  const setCallResultGroup = (text) => {
    _setCallResultGroup(text);
    callResultGroupRef.current = text || "";
  };

  const onNotesInputChange = (text) => {
    const next = text || "";
    setNotes(next);
    const activeCallId = activeConnectedCallIdRef?.current;
    if (activeCallId && callMapRef?.current?.has(activeCallId)) {
      upsertCallMap(activeCallId, { notes: next });
    }
  };

  const setSubject = (text) => {
    _setSubject(text);
    subjectRef.current = text || "";
  };

  const onSubjectInputChange = (text) => {
    const next = text || "";
    setSubject(next);
    const activeCallId = activeConnectedCallIdRef?.current;
    if (activeCallId && callMapRef?.current?.has(activeCallId)) {
      upsertCallMap(activeCallId, { subject: next });
    }
  };

  const onCallResultInputChange = (text) => {
    const next = text || "";
    setCallResult(next);
    const activeCallId = activeConnectedCallIdRef?.current;
    if (activeCallId && callMapRef?.current?.has(activeCallId)) {
      upsertCallMap(activeCallId, { callResult: next });
    }
  };

  const onCallResultGroupInputChange = (text) => {
    const next = text || "";
    setCallResultGroup(next);
    const activeCallId = activeConnectedCallIdRef?.current;
    if (activeCallId && callMapRef?.current?.has(activeCallId)) {
      upsertCallMap(activeCallId, { callResultGroup: next });
    }
  };

  const setActiveConnectedCallId = (text) => {
    _setActiveConnectedCallId(text);
    activeConnectedCallIdRef.current = text || "";
  };

  const setWrapupConnectedCallId = (text) => {
    _setWrapupConnectedCallId(text);
    wrapupConnectedCallIdRef.current = text || "";
  };

  const setDeletedAssignedRecId = (text) => {
    deletedAssignedRecIdRef.current = text || "";
  };

  const setPresenceInTeamsClient = async (state) => {
    try {
      // Session-based: use authenticatedFetch which includes session cookie and CSRF token
      const response = await authenticatedFetch("/api/setPresence", {
        method: "POST",
        body: JSON.stringify({ state }),
      });

      // Parse JSON response from server
      const result = await response.json();

      if (response.ok) {
        // Success : res ok is true for 2xx statuses (like 200, 204).
        console.log("Presence updated:", result);
        return result;
      } else if (response.status === 401 || result?.success === false) {
        // Unauthorized -> session expired
        console.warn("Unauthorized, session expired - need to reauthenticate");
        return {
          success: false,
          message: "Session expired",
          error: "SESSION_EXPIRED",
        };
      } else {
        console.error("Error setting presence:", result);
        return { success: false, message: "Graph call failed", error: result };
      }
    } catch (err) {
      console.error("Network or server error:", err);
      return { success: false, message: "Network or server error", error: err };
    }
  };

  return {
    teamsCallAgent,
    deviceManager,
    call,
    incomingCall,
    activeConnectedCallIdRef,
    callMapRef,
    incomingCallMapRef,
    upsertIncomingCallMap,
    callMap,
    incomingCallMap,
    upsertCallMap,
    transferCallMap,
    removeFromTransferCallMap,
    setActiveConnectedCallId,
    activeConnectedCallId,
    setWrapupConnectedCallId,
    wrapupConnectedCallId,
    wrapupConnectedCallIdRef,
    wrapupDone,
    wrapupEdit,
    callState,
    isSwitchingCall,
    isMuted,
    errorMessage,
    notes,
    login: () => {}, // Login handled by App component
    logout,
    placeCall,
    acceptCall,
    hangUp,
    mute,
    unmute,
    setNotes,
    onNotesInputChange,
    subject,
    setSubject,
    onSubjectInputChange,
    showEditSubject,
    callResult,
    setCallResult,
    callResultGroup,
    setCallResultGroup,
    onCallResultInputChange,
    onCallResultGroupInputChange,
    callerInfo,
    getActiveCall,
    switchActiveCall,
    //getPresenceStateLabel,
    setPresenceInTeamsClient,
    blindTransfer,
    consultedTransfer,
    cancelTransfer,
    callTransferState,
    onAnotherDevice,
    isHold,
    onHold,
    onResume,
    handleDtmf,
    transferorInfo,
    lastEntityIdRef,
    assignMetaRef,
    setDeletedAssignedRecId,
    deletedAssignedRecIdRef,
    pickCallId,
    activeCallDet,
    callStartTimeRef,
  };
}

export default useACSClient;
